import React, { useState, useEffect, useCallback } from 'react';
import { getNotificationActivityFeed, getNotificationDetail } from '../../utils/adminApi';
import { DELIVERY_STATUS_COLORS } from './workflowConfig';
import '../../styles/NotificationActivityFeed.css';

const PAGE_SIZE = 50;

function getRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTimestamp(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TYPE_COLORS = {
  campaign: { bg: '#dbeafe', text: '#2563eb' },
  auto: { bg: '#dcfce7', text: '#16a34a' },
  inquiry: { bg: '#fae8ff', text: '#a21caf' },
  reminder: { bg: '#fef3c7', text: '#d97706' },
};

const TypeBadge = ({ type }) => {
  const colors = TYPE_COLORS[type] || { bg: '#e5e7eb', text: '#6b7280' };
  return (
    <span
      className="notif-feed-type-badge"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      {type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown'}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const color = DELIVERY_STATUS_COLORS[status] || '#6b7280';
  return (
    <span className="notif-feed-status-inline">
      <span className="notif-feed-status-dot" style={{ backgroundColor: color }} />
      <span style={{ color, fontSize: '0.75rem', textTransform: 'capitalize' }}>{status}</span>
    </span>
  );
};

const TimelineStep = ({ label, timestamp, color }) => (
  <div className="notif-feed-timeline-step">
    <div className="notif-feed-timeline-dot" style={{ backgroundColor: timestamp ? color : '#d1d5db' }} />
    <div className="notif-feed-timeline-info">
      <span className="notif-feed-timeline-label" style={{ color: timestamp ? '#374151' : '#9ca3af' }}>
        {label}
      </span>
      {timestamp && (
        <span className="notif-feed-timeline-time">{formatTimestamp(timestamp)}</span>
      )}
    </div>
  </div>
);

const ExpandedDetail = ({ item, detail, loading }) => {
  if (loading) {
    return (
      <div className="notif-feed-row-detail">
        <div className="notif-feed-detail-loading">Loading details...</div>
      </div>
    );
  }

  const record = detail || item;

  return (
    <div className="notif-feed-row-detail">
      {item.channel === 'email' ? (
        <div className="notif-feed-detail-email">
          <div className="notif-feed-detail-section">
            <div className="notif-feed-detail-label">Delivery Timeline</div>
            <div className="notif-feed-timeline">
              <TimelineStep label="Sent" timestamp={record.createdAt} color="#a78bfa" />
              <div className="notif-feed-timeline-connector" />
              <TimelineStep label="Delivered" timestamp={record.deliveredAt} color="#4ade80" />
              <div className="notif-feed-timeline-connector" />
              <TimelineStep label="Opened" timestamp={record.openedAt} color="#fbbf24" />
              <div className="notif-feed-timeline-connector" />
              <TimelineStep label="Clicked" timestamp={record.clickedAt} color="#f472b6" />
              {record.bouncedAt && (
                <>
                  <div className="notif-feed-timeline-connector" />
                  <TimelineStep label="Bounced" timestamp={record.bouncedAt} color="#f87171" />
                </>
              )}
            </div>
          </div>
          <div className="notif-feed-detail-stats">
            <div className="notif-feed-detail-stat">
              <span className="notif-feed-detail-stat-label">Opens</span>
              <span className="notif-feed-detail-stat-value">{record.openCount ?? 0}</span>
            </div>
            <div className="notif-feed-detail-stat">
              <span className="notif-feed-detail-stat-label">Clicks</span>
              <span className="notif-feed-detail-stat-value">{record.clickCount ?? 0}</span>
            </div>
            {record.recipient && (
              <div className="notif-feed-detail-stat">
                <span className="notif-feed-detail-stat-label">Recipient</span>
                <span className="notif-feed-detail-stat-value">{record.recipient}</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="notif-feed-detail-push">
          <div className="notif-feed-detail-stats">
            <div className="notif-feed-detail-stat">
              <span className="notif-feed-detail-stat-label">Device Type</span>
              <span className="notif-feed-detail-stat-value">{record.deviceType || 'Unknown'}</span>
            </div>
            <div className="notif-feed-detail-stat">
              <span className="notif-feed-detail-stat-label">Delivery Status</span>
              <span className="notif-feed-detail-stat-value" style={{ color: DELIVERY_STATUS_COLORS[record.status] || '#aaa' }}>
                {record.status}
              </span>
            </div>
            {record.deliveredAt && (
              <div className="notif-feed-detail-stat">
                <span className="notif-feed-detail-stat-label">Delivered At</span>
                <span className="notif-feed-detail-stat-value">{formatTimestamp(record.deliveredAt)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ActivityFeedTab = () => {
  const [filters, setFilters] = useState({
    channel: 'all',
    status: '',
    type: '',
    search: '',
    timeRange: '30d',
  });
  const [page, setPage] = useState(0);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [detailMap, setDetailMap] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const apiFilters = {
        channel: filters.channel !== 'all' ? filters.channel : undefined,
        status: filters.status || undefined,
        type: filters.type || undefined,
        search: filters.search || undefined,
        timeRange: filters.timeRange,
      };
      const pagination = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      const result = await getNotificationActivityFeed(apiFilters, pagination);
      setItems(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Error loading activity feed:', err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  // Reset page on filter change
  const updateFilter = (key, value) => {
    setPage(0);
    setExpandedRow(null);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRowClick = async (item) => {
    if (expandedRow === item.id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(item.id);
    if (!detailMap[item.id]) {
      setDetailLoading(true);
      try {
        const detail = await getNotificationDetail(item.id, item.channel);
        setDetailMap(prev => ({ ...prev, [item.id]: detail }));
      } catch (err) {
        console.error('Error loading notification detail:', err);
        setDetailMap(prev => ({ ...prev, [item.id]: item }));
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const startIdx = page * PAGE_SIZE + 1;
  const endIdx = Math.min((page + 1) * PAGE_SIZE, total);

  const getPageButtons = () => {
    const pages = [];
    const start = Math.max(0, page - 2);
    const end = Math.min(totalPages - 1, page + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="notif-feed">
      {/* Filter bar */}
      <div className="notif-feed-filters">
        {/* Channel toggle */}
        <div className="notif-feed-filter-group">
          {['all', 'email', 'push'].map(ch => (
            <button
              key={ch}
              className={`notif-feed-filter-btn ${filters.channel === ch ? 'active' : ''}`}
              onClick={() => updateFilter('channel', ch)}
            >
              {ch === 'all' ? 'All' : ch === 'email' ? '📧 Email' : '📱 Push'}
            </button>
          ))}
        </div>

        {/* Status dropdown */}
        <select
          className="notif-feed-select"
          value={filters.status}
          onChange={e => updateFilter('status', e.target.value)}
        >
          <option value="">All Status</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
        </select>

        {/* Type dropdown */}
        <select
          className="notif-feed-select"
          value={filters.type}
          onChange={e => updateFilter('type', e.target.value)}
        >
          <option value="">All Types</option>
          <option value="campaign">Campaign</option>
          <option value="auto">Auto</option>
          <option value="inquiry">Inquiry</option>
        </select>

        {/* Search */}
        <div className="notif-feed-search-wrap">
          <span className="notif-feed-search-icon">🔍</span>
          <input
            className="notif-feed-search"
            type="text"
            placeholder="Search by recipient..."
            value={filters.search}
            onChange={e => updateFilter('search', e.target.value)}
          />
        </div>

        {/* Time range */}
        <div className="notif-feed-filter-group">
          {['7d', '30d', '90d'].map(range => (
            <button
              key={range}
              className={`notif-feed-filter-btn ${filters.timeRange === range ? 'active' : ''}`}
              onClick={() => updateFilter('timeRange', range)}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="notif-feed-table">
        {/* Header */}
        <div className="notif-feed-table-header">
          <div />
          <div>Notification</div>
          <div>Recipient</div>
          <div>Type</div>
          <div>Channel</div>
          <div>Status</div>
          <div>Time</div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="notif-feed-loading">Loading activity feed...</div>
        ) : items.length === 0 ? (
          <div className="notif-feed-empty">No notifications found for this filter.</div>
        ) : (
          items.map(item => (
            <React.Fragment key={item.id}>
              <div
                className={`notif-feed-row ${expandedRow === item.id ? 'expanded' : ''}`}
                onClick={() => handleRowClick(item)}
              >
                {/* Expand toggle */}
                <div className="notif-feed-expand-toggle">
                  {expandedRow === item.id ? '▼' : '▶'}
                </div>

                {/* Title + subtitle */}
                <div className="notif-feed-row-title-col">
                  <div className="notif-feed-row-title">{item.title}</div>
                  {item.subtitle && (
                    <div className="notif-feed-row-subtitle">{item.subtitle}</div>
                  )}
                </div>

                {/* Recipient */}
                <div className="notif-feed-row-recipient">
                  {item.recipient || (item.type === 'campaign' ? 'N recipients' : '—')}
                </div>

                {/* Type badge */}
                <div>
                  <TypeBadge type={item.type} />
                </div>

                {/* Channel icon */}
                <div className="notif-feed-row-channel">
                  {item.channel === 'email' ? '📧' : '📱'}
                </div>

                {/* Status */}
                <div>
                  <StatusDot status={item.status} />
                </div>

                {/* Time */}
                <div className="notif-feed-row-time">
                  {getRelativeTime(item.createdAt)}
                </div>
              </div>

              {expandedRow === item.id && (
                <ExpandedDetail
                  item={item}
                  detail={detailMap[item.id]}
                  loading={detailLoading && !detailMap[item.id]}
                />
              )}
            </React.Fragment>
          ))
        )}
      </div>

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="notif-feed-pagination">
          <span className="notif-feed-pagination-info">
            Showing {startIdx}–{endIdx} of {total.toLocaleString()} notifications
          </span>
          <div className="notif-feed-pagination-controls">
            <button
              className="notif-feed-page-btn"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              Prev
            </button>
            {getPageButtons().map(p => (
              <button
                key={p}
                className={`notif-feed-page-btn ${p === page ? 'active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p + 1}
              </button>
            ))}
            <button
              className="notif-feed-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityFeedTab;
